import { FormikErrors } from 'formik';

/**
 * What a mask can say about a part of itself that is not ready yet.
 *
 * `tab` is the heading the user sees above the form, `errors` what Formik found
 * in it. A part the user cannot see still counts: its values live in the store
 * whether its form is mounted or not, which is why a save could be refused over
 * a field on a tab nobody was looking at (#2212, R-28).
 */
export interface PaloIncompletePart {
    /** Translation id of the tab's heading, e.g. "app.navigation.details". */
    tab: string;
    errors?: FormikErrors<unknown> | Record<string, unknown>;
    /**
     * Where a field is not labelled under `app.label.<field>`.
     *
     * A supplier's `name` is "Firmenname", a person's is not, so the generic id
     * cannot carry both and the mask says which one it means.
     */
    labels?: Record<string, string>;
}

/** A field that is missing, with the id its label is translated under. */
export interface PaloMissingField {
    tab: string;
    field: string;
    /** `app.label.<field>` where that exists; the mask may override it. */
    labelId: string;
}

/**
 * Formik nests errors the way it nests values, so an address inside a contact
 * arrives as `addresses[0].street`. The leaf is what carries a label.
 */
const leaves = (errors: unknown, path: string[] = []): string[][] => {
    if (!errors) {
        return [];
    }

    if ('string' === typeof errors) {
        return [path];
    }

    if (Array.isArray(errors)) {
        return errors.flatMap((entry, index) => leaves(entry, [...path, String(index)]));
    }

    if ('object' === typeof errors) {
        return Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) =>
            leaves(value, [...path, key])
        );
    }

    return [];
};

/** The last part of the path that is a name rather than an index. */
const fieldOf = (path: string[]): string => [...path].reverse().find(part => !/^\d+$/.test(part)) ?? path.join('.');

/**
 * Which fields keep a mask from being saved, and on which tab they sit.
 *
 * The same list serves the message after a refused save and the hint on a main
 * action that is blocked, so both say the same thing (#2212 R-28, #2210 R-98).
 */
export const paloMissingFields = (parts: PaloIncompletePart[]): PaloMissingField[] =>
    parts.flatMap(part => {
        const seen = new Set<string>();

        return leaves(part.errors)
            .map(fieldOf)
            .filter(field => {
                if (seen.has(field)) {
                    return false;
                }
                seen.add(field);

                return true;
            })
            .map(field => ({
                tab: part.tab,
                field,
                labelId: part.labels?.[field] ?? `app.label.${field}`,
            }));
    });
